import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const CustomerManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State for customer list
  const [customers, setCustomers] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterLoanId, setFilterLoanId] = useState('');
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  
  // State for customer details view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  
  // State for edit request
  const [editRequestDialogOpen, setEditRequestDialogOpen] = useState(false);
  const [editRequestReason, setEditRequestReason] = useState('');
  
  // State for delete request
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  
  // State for approved request and restricted editing
  const [approvedRequest, setApprovedRequest] = useState(null);
  const [allowedFields, setAllowedFields] = useState([]);
  const [isRestrictedEditMode, setIsRestrictedEditMode] = useState(false);

  useEffect(() => {
    // Always ensure edit mode is disabled when component loads or id changes
    setIsRestrictedEditMode(false);
    setApprovedRequest(null);
    setAllowedFields([]);
    
    fetchAreas();
    
    if (id) {
      fetchCustomerDetails();
    } else {
      fetchCustomers();
    }
  }, [id]);
  
  const fetchAreas = async () => {
    try {
      setLoadingAreas(true);
      const response = await api.get('/officer/areas/active');
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
    } finally {
      setLoadingAreas(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (filterName) params.append('name', filterName);
      if (filterLoanId) params.append('loanId', filterLoanId);
      
      const url = params.toString() ? `/officer/customers?${params.toString()}` : '/officer/customers';
      const response = await api.get(url);
      setCustomers(response.data || []);
      setMessage('');
    } catch (error) {
      console.error('Error fetching customers:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Network Error';
      setMessage('Error fetching customers: ' + errorMessage);
      setCustomers([]);
    }
  };

  const fetchCustomerDetails = async () => {
    if (!id) {
      setMessage('Error: Customer ID not provided');
      return;
    }
    
    try {
      // Reset edit mode and approved request state when loading a new customer
      setIsRestrictedEditMode(false);
      setApprovedRequest(null);
      setAllowedFields([]);
      
      const response = await api.get(`/officer/customers/${id}`);
      const customerData = response.data;
      
      if (!customerData) {
        setMessage('Error: Customer not found');
        setSelectedCustomer(null);
        return;
      }
      
      setSelectedCustomer(customerData);
      
      // Initialize form data
      const initialFormData = {
        fullName: customerData.fullName || '',
        address: customerData.address || '',
        businessAddress: customerData.businessAddress || '',
        gramaNiladhariDivision: customerData.gramaNiladhariDivision || '',
        nic: customerData.nic || '',
        birthdate: customerData.birthdate || '',
        occupation: customerData.occupation || '',
        registrationPeriod: customerData.registrationPeriod || '',
        phoneNumber: customerData.phoneNumber || '',
        spouseFullName: customerData.spouseFullName || '',
        spouseNic: customerData.spouseNic || '',
        spousePhoneNumber: customerData.spousePhoneNumber || '',
        numberOfDependents: customerData.numberOfDependents || '',
        assetsLands: customerData.assetsLands || '',
        assetsVehicles: customerData.assetsVehicles || '',
        assetsOther: customerData.assetsOther || '',
        bankAccountNumber: customerData.bankAccountNumber || '',
        bankBranch: customerData.bankBranch || '',
        bankName: customerData.bankName || '',
        guarantorName: customerData.guarantorName || '',
        guarantorAddress: customerData.guarantorAddress || '',
        guarantorBusinessAddress: customerData.guarantorBusinessAddress || '',
        guarantorOccupation: customerData.guarantorOccupation || '',
        guarantorNic: customerData.guarantorNic || '',
        guarantorPhoneNumber: customerData.guarantorPhoneNumber || '',
        areaId: customerData.area?.id || customerData.areaId || '',
      };
      setFormData(initialFormData);
      
      // Check for approved edit requests
      if (customerData.id && user && user.userId) {
        await checkApprovedRequests(customerData.id);
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      setMessage('Error fetching customer: ' + errorMsg);
      setSelectedCustomer(null);
    }
  };
  
  const checkApprovedRequests = async (customerId) => {
    if (!user || !user.userId) {
      console.warn('User not available for checking approved requests');
      setApprovedRequest(null);
      setAllowedFields([]);
      return;
    }
    
    try {
      const response = await api.get('/officer/customer-requests', {
        params: { officerId: user.userId }
      });
      const requests = response.data || [];
      
      // Find APPROVED_FOR_EDIT request for this customer (exclude COMPLETED)
      const request = requests.find(r => 
        r.customer?.id === customerId && 
        r.status === 'APPROVED_FOR_EDIT'
      );
      
      if (request && request.allowedFields) {
        try {
          const fields = JSON.parse(request.allowedFields);
          setApprovedRequest(request);
          setAllowedFields(Array.isArray(fields) ? fields : []);
        } catch (e) {
          console.error('Error parsing allowed fields:', e);
          setApprovedRequest(null);
          setAllowedFields([]);
        }
      } else {
        // Explicitly set to null if no approved request found - this ensures Request Edit button shows
        setApprovedRequest(null);
        setAllowedFields([]);
      }
    } catch (error) {
      console.error('Error checking approved requests:', error);
      // On error, ensure we don't block the Request Edit button
      setApprovedRequest(null);
      setAllowedFields([]);
    }
  };

  const handleRequestEdit = () => {
    setEditRequestReason('');
    setEditRequestDialogOpen(true);
  };

  const handleSubmitEditRequest = async () => {
    if (!editRequestReason.trim()) {
      setMessage('Please provide a reason for the edit request');
      return;
    }
    
    if (!user || !user.userId) {
      setMessage('Error: User information not available');
      return;
    }
    
    if (!selectedCustomer || !selectedCustomer.id) {
      setMessage('Error: Customer information not available');
      return;
    }
    
    try {
      // Create edit request - send current form data as updatedData
      await api.put(`/officer/customers/${selectedCustomer.id}`, formData, {
        params: {
          officerId: user.userId,
          reason: editRequestReason.trim(),
        },
      });
      setMessage('Edit request submitted for admin approval. You will be notified once it is reviewed.');
      setEditRequestDialogOpen(false);
      setEditRequestReason('');
      await fetchCustomerDetails();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'Unknown error occurred';
      setMessage('Error submitting edit request: ' + errorMsg);
      console.error('Edit request error:', error.response?.data || error);
    }
  };

  const handleStartRestrictedEdit = () => {
    if (approvedRequest && approvedRequest.status === 'APPROVED_FOR_EDIT' && allowedFields.length > 0) {
      setIsRestrictedEditMode(true);
      setMessage('');
    } else {
      setMessage('Error: No approved edit request found or no fields approved for editing');
    }
  };

  const handleCancelRestrictedEdit = () => {
    setIsRestrictedEditMode(false);
    setMessage('');
    fetchCustomerDetails(); // Reset form data to original values
  };

  const handleSaveRestrictedEdit = async () => {
    try {
      // Prepare clean customer data
      const cleanFormData = { ...formData };
      delete cleanFormData.id;
      delete cleanFormData.createdAt;
      delete cleanFormData.loans;
      delete cleanFormData.updatedAt;
      
      // Convert empty strings to null for optional fields
      const optionalFields = [
        'businessAddress', 'gramaNiladhariDivision', 'occupation', 'registrationPeriod',
        'areaId', 'numberOfDependents', 'assetsLands', 'assetsVehicles', 'assetsOther',
        'bankAccountNumber', 'bankBranch', 'bankName',
        'guarantorName', 'guarantorAddress', 'guarantorBusinessAddress',
        'guarantorOccupation', 'guarantorNic', 'guarantorPhoneNumber',
        'salarySlipPath', 'idDocumentPath', 'photoPath', 'birthdate'
      ];
      
      optionalFields.forEach(field => {
        if (cleanFormData[field] === '' || cleanFormData[field] === undefined) {
          cleanFormData[field] = null;
        }
      });
      
      // Apply restricted update
      await api.post(`/officer/customer-requests/${approvedRequest.id}/apply-restricted-update`, cleanFormData);
      setMessage('Customer updated successfully! All fields are now locked again.');
      setIsRestrictedEditMode(false);
      setApprovedRequest(null);
      setAllowedFields([]);
      await fetchCustomerDetails(); // Refresh to get updated data
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'Unknown error occurred';
      setMessage('Error updating customer: ' + errorMsg);
      console.error('Update error:', error.response?.data || error);
    }
  };

  const handleDeleteRequest = async () => {
    if (!deleteReason.trim()) {
      setMessage('Please provide a reason for deletion');
      return;
    }
    
    if (!user || !user.userId) {
      setMessage('Error: User information not available');
      return;
    }
    
    if (!selectedCustomer || !selectedCustomer.id) {
      setMessage('Error: Customer information not available');
      return;
    }
    
    try {
      await api.post(`/officer/customers/${selectedCustomer.id}/delete-request`, null, {
        params: {
          officerId: user.userId,
          reason: deleteReason.trim(),
        },
      });
      setMessage('Delete request submitted for admin approval.');
      setDeleteDialogOpen(false);
      setDeleteReason('');
      navigate('/officer/customers');
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'Unknown error occurred';
      setMessage('Error submitting delete request: ' + errorMsg);
      console.error('Delete request error:', error.response?.data || error);
    }
  };

  const isFieldEditable = (fieldName) => {
    // Field is editable ONLY if:
    // 1. In restricted edit mode (officer clicked "Edit Approved Fields")
    // 2. AND field is in the allowed fields list (admin approved this field)
    // Otherwise, field is always disabled (no direct editing allowed)
    
    // CRITICAL: Always return false if not in restricted edit mode
    if (!isRestrictedEditMode) {
      return false;
    }
    
    // CRITICAL: Always return false if no allowed fields
    if (!Array.isArray(allowedFields) || allowedFields.length === 0) {
      return false;
    }
    
    // Only return true if field is in the allowed list
    return allowedFields.includes(fieldName);
  };

  const handleFilter = () => {
    fetchCustomers();
  };

  const handleClearFilter = () => {
    setFilterName('');
    setFilterLoanId('');
    fetchCustomers();
  };

  // Customer Details View
  if (id && selectedCustomer) {
    // Debug: Log state to help identify issues
    console.log('CustomerManagement Render State:', {
      id,
      hasSelectedCustomer: !!selectedCustomer,
      isRestrictedEditMode,
      hasApprovedRequest: !!approvedRequest,
      approvedRequestStatus: approvedRequest?.status,
      allowedFieldsCount: allowedFields.length
    });

    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/officer/customers')} sx={{ mb: 2 }}>
          Back to Customers
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Customer Details</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isRestrictedEditMode ? (
              <>
                {/* Show "Edit Approved Fields" only if we have a valid approved request with fields */}
                {approvedRequest?.status === 'APPROVED_FOR_EDIT' && Array.isArray(allowedFields) && allowedFields.length > 0 ? (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<EditIcon />}
                    onClick={handleStartRestrictedEdit}
                  >
                    Edit Approved Fields
                  </Button>
                ) : (
                  /* Always show "Request Edit" button when not in restricted edit mode */
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleRequestEdit}
                    sx={{ minWidth: 140 }}
                  >
                    Request Edit
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Request Deletion
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={handleCancelRestrictedEdit}
              >
                Cancel Edit
              </Button>
            )}
          </Box>
        </Box>

        {message && (
          <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage('')}>
            {message}
          </Alert>
        )}

        {!isRestrictedEditMode && !approvedRequest && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>⚠️ Read-Only Mode:</strong> Customer details cannot be edited directly. All edits require admin approval.
            <br />
            Click "Request Edit" to submit an edit request to the admin.
          </Alert>
        )}

        {approvedRequest && approvedRequest.status === 'APPROVED_FOR_EDIT' && allowedFields.length > 0 && !isRestrictedEditMode && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>✓ Edit Request Approved!</strong> Admin has approved editing for the following fields: <strong>{allowedFields.map(key => {
              const fieldLabels = {
                'fullName': 'Full Name',
                'nic': 'NIC',
                'address': 'Address',
                'businessAddress': 'Business Address',
                'gramaNiladhariDivision': 'Grama Niladhari Division',
                'areaId': 'Area',
                'birthdate': 'Birthdate',
                'occupation': 'Occupation',
                'registrationPeriod': 'Registration Period',
                'phoneNumber': 'Phone Number',
                'spouseFullName': 'Spouse Full Name',
                'spouseNic': 'Spouse NIC',
                'spousePhoneNumber': 'Spouse Phone Number',
                'numberOfDependents': 'Number of Dependents',
                'assetsLands': 'Assets - Lands',
                'assetsVehicles': 'Assets - Vehicles',
                'assetsOther': 'Assets - Other',
                'bankAccountNumber': 'Bank Account Number',
                'bankBranch': 'Bank Branch',
                'bankName': 'Bank Name',
                'guarantorName': 'Guarantor Name',
                'guarantorAddress': 'Guarantor Address',
                'guarantorBusinessAddress': 'Guarantor Business Address',
                'guarantorOccupation': 'Guarantor Occupation',
                'guarantorNic': 'Guarantor NIC',
                'guarantorPhoneNumber': 'Guarantor Phone Number'
              };
              return fieldLabels[key] || key;
            }).join(', ')}</strong>
            <br />
            Click "Edit Approved Fields" to start editing. Only the approved fields will be editable.
          </Alert>
        )}

        {isRestrictedEditMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Restricted Edit Mode:</strong> You can only edit the following approved fields: <strong>{allowedFields.join(', ')}</strong>
            <br />
            All other fields are disabled.
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Personal Information
            </Typography>
            {!isRestrictedEditMode && (
              <Chip 
                label="Read-Only Mode" 
                color="warning" 
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={formData.fullName || ''}
                onChange={(e) => {
                  if (isFieldEditable('fullName')) {
                    setFormData({ ...formData, fullName: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('fullName')}
                InputProps={{
                  readOnly: !isFieldEditable('fullName')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NIC"
                required
                value={formData.nic || ''}
                onChange={(e) => {
                  if (isFieldEditable('nic')) {
                    setFormData({ ...formData, nic: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('nic')}
                InputProps={{
                  readOnly: !isFieldEditable('nic')
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                required
                value={formData.address || ''}
                onChange={(e) => {
                  if (isFieldEditable('address')) {
                    setFormData({ ...formData, address: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('address')}
                InputProps={{
                  readOnly: !isFieldEditable('address')
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Address"
                value={formData.businessAddress || ''}
                onChange={(e) => {
                  if (isFieldEditable('businessAddress')) {
                    setFormData({ ...formData, businessAddress: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('businessAddress')}
                InputProps={{
                  readOnly: !isFieldEditable('businessAddress')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grama Niladhari Division"
                value={formData.gramaNiladhariDivision || ''}
                onChange={(e) => {
                  if (isFieldEditable('gramaNiladhariDivision')) {
                    setFormData({ ...formData, gramaNiladhariDivision: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('gramaNiladhariDivision')}
                InputProps={{
                  readOnly: !isFieldEditable('gramaNiladhariDivision')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Area"
                value={formData.areaId || ''}
                onChange={(e) => {
                  if (isFieldEditable('areaId')) {
                    setFormData({ ...formData, areaId: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('areaId') || loadingAreas}
                helperText={loadingAreas ? 'Loading areas...' : 'Select the area for this customer'}
              >
                {loadingAreas ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading...
                  </MenuItem>
                ) : areas.length === 0 ? (
                  <MenuItem disabled>No areas available</MenuItem>
                ) : (
                  areas.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      {area.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Birthdate"
                type="date"
                value={formData.birthdate || ''}
                onChange={(e) => {
                  if (isFieldEditable('birthdate')) {
                    setFormData({ ...formData, birthdate: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('birthdate')}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: !isFieldEditable('birthdate')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Occupation / Business"
                value={formData.occupation || ''}
                onChange={(e) => {
                  if (isFieldEditable('occupation')) {
                    setFormData({ ...formData, occupation: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('occupation')}
                InputProps={{
                  readOnly: !isFieldEditable('occupation')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Period"
                value={formData.registrationPeriod || ''}
                onChange={(e) => {
                  if (isFieldEditable('registrationPeriod')) {
                    setFormData({ ...formData, registrationPeriod: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('registrationPeriod')}
                InputProps={{
                  readOnly: !isFieldEditable('registrationPeriod')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                required
                value={formData.phoneNumber || ''}
                onChange={(e) => {
                  if (isFieldEditable('phoneNumber')) {
                    setFormData({ ...formData, phoneNumber: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('phoneNumber')}
                InputProps={{
                  readOnly: !isFieldEditable('phoneNumber')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Full Name of Husband/Wife"
                required
                value={formData.spouseFullName || ''}
                onChange={(e) => {
                  if (isFieldEditable('spouseFullName')) {
                    setFormData({ ...formData, spouseFullName: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('spouseFullName')}
                InputProps={{
                  readOnly: !isFieldEditable('spouseFullName')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="NIC of Husband/Wife"
                required
                value={formData.spouseNic || ''}
                onChange={(e) => {
                  if (isFieldEditable('spouseNic')) {
                    setFormData({ ...formData, spouseNic: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('spouseNic')}
                InputProps={{
                  readOnly: !isFieldEditable('spouseNic')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Phone Number of Husband/Wife"
                required
                value={formData.spousePhoneNumber || ''}
                onChange={(e) => {
                  if (isFieldEditable('spousePhoneNumber')) {
                    setFormData({ ...formData, spousePhoneNumber: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('spousePhoneNumber')}
                InputProps={{
                  readOnly: !isFieldEditable('spousePhoneNumber')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Number of Dependents"
                type="number"
                value={formData.numberOfDependents || ''}
                onChange={(e) => {
                  if (isFieldEditable('numberOfDependents')) {
                    setFormData({ ...formData, numberOfDependents: e.target.value ? parseInt(e.target.value) : null });
                  }
                }}
                disabled={!isFieldEditable('numberOfDependents')}
                InputProps={{
                  readOnly: !isFieldEditable('numberOfDependents')
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Assets
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Lands"
                value={formData.assetsLands || ''}
                onChange={(e) => {
                  if (isFieldEditable('assetsLands')) {
                    setFormData({ ...formData, assetsLands: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('assetsLands')}
                InputProps={{
                  readOnly: !isFieldEditable('assetsLands')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Vehicles"
                value={formData.assetsVehicles || ''}
                onChange={(e) => {
                  if (isFieldEditable('assetsVehicles')) {
                    setFormData({ ...formData, assetsVehicles: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('assetsVehicles')}
                InputProps={{
                  readOnly: !isFieldEditable('assetsVehicles')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Other"
                value={formData.assetsOther || ''}
                onChange={(e) => {
                  if (isFieldEditable('assetsOther')) {
                    setFormData({ ...formData, assetsOther: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('assetsOther')}
                InputProps={{
                  readOnly: !isFieldEditable('assetsOther')
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Bank Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={formData.bankAccountNumber || ''}
                onChange={(e) => {
                  if (isFieldEditable('bankAccountNumber')) {
                    setFormData({ ...formData, bankAccountNumber: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('bankAccountNumber')}
                InputProps={{
                  readOnly: !isFieldEditable('bankAccountNumber')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Branch"
                value={formData.bankBranch || ''}
                onChange={(e) => {
                  if (isFieldEditable('bankBranch')) {
                    setFormData({ ...formData, bankBranch: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('bankBranch')}
                InputProps={{
                  readOnly: !isFieldEditable('bankBranch')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bankName || ''}
                onChange={(e) => {
                  if (isFieldEditable('bankName')) {
                    setFormData({ ...formData, bankName: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('bankName')}
                InputProps={{
                  readOnly: !isFieldEditable('bankName')
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Guarantor Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor Name"
                value={formData.guarantorName || ''}
                onChange={(e) => {
                  if (isFieldEditable('guarantorName')) {
                    setFormData({ ...formData, guarantorName: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('guarantorName')}
                InputProps={{
                  readOnly: !isFieldEditable('guarantorName')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor NIC"
                value={formData.guarantorNic || ''}
                onChange={(e) => {
                  if (isFieldEditable('guarantorNic')) {
                    setFormData({ ...formData, guarantorNic: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('guarantorNic')}
                InputProps={{
                  readOnly: !isFieldEditable('guarantorNic')
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guarantor Address"
                value={formData.guarantorAddress || ''}
                onChange={(e) => {
                  if (isFieldEditable('guarantorAddress')) {
                    setFormData({ ...formData, guarantorAddress: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('guarantorAddress')}
                InputProps={{
                  readOnly: !isFieldEditable('guarantorAddress')
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guarantor Business Address"
                value={formData.guarantorBusinessAddress || ''}
                onChange={(e) => {
                  if (isFieldEditable('guarantorBusinessAddress')) {
                    setFormData({ ...formData, guarantorBusinessAddress: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('guarantorBusinessAddress')}
                InputProps={{
                  readOnly: !isFieldEditable('guarantorBusinessAddress')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor Occupation"
                value={formData.guarantorOccupation || ''}
                onChange={(e) => {
                  if (isFieldEditable('guarantorOccupation')) {
                    setFormData({ ...formData, guarantorOccupation: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('guarantorOccupation')}
                InputProps={{
                  readOnly: !isFieldEditable('guarantorOccupation')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor Phone Number"
                value={formData.guarantorPhoneNumber || ''}
                onChange={(e) => {
                  if (isFieldEditable('guarantorPhoneNumber')) {
                    setFormData({ ...formData, guarantorPhoneNumber: e.target.value });
                  }
                }}
                disabled={!isFieldEditable('guarantorPhoneNumber')}
                InputProps={{
                  readOnly: !isFieldEditable('guarantorPhoneNumber')
                }}
              />
            </Grid>
          </Grid>

          {isRestrictedEditMode && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleCancelRestrictedEdit}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveRestrictedEdit} startIcon={<SaveIcon />}>
                Save Changes
              </Button>
            </Box>
          )}
        </Paper>

        {/* Edit Request Dialog */}
        <Dialog open={editRequestDialogOpen} onClose={() => setEditRequestDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Request Edit Permission</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              All customer edits require admin approval. Please provide a reason for your edit request.
            </Alert>
            <TextField
              fullWidth
              label="Reason for Edit Request (Required)"
              multiline
              rows={4}
              required
              value={editRequestReason}
              onChange={(e) => setEditRequestReason(e.target.value)}
              placeholder="Please provide a detailed reason for editing this customer's information..."
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRequestDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitEditRequest} 
              variant="contained" 
              startIcon={<SendIcon />}
              disabled={!editRequestReason.trim()}
            >
              Submit Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Request Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Request Customer Deletion</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Customer deletion requires admin approval. This action cannot be undone once approved.
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Customer:</strong> {selectedCustomer?.fullName || selectedCustomer?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>NIC:</strong> {selectedCustomer?.nic}
            </Typography>
            <TextField
              fullWidth
              label="Reason for Deletion (Required)"
              multiline
              rows={4}
              required
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Please provide a detailed reason for deleting this customer..."
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteRequest} variant="contained" color="error" disabled={!deleteReason.trim()}>
              Submit Delete Request
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Customer list view
  return (
    <Box>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontWeight: 700,
          color: '#0D47A1',
          mb: 3,
          fontSize: { xs: '1.75rem', sm: '2rem' },
        }}
      >
        Customer Management
      </Typography>
      {message && (
        <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      
      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filter Customers
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Customer Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Enter customer name..."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Loan ID"
              type="number"
              value={filterLoanId}
              onChange={(e) => setFilterLoanId(e.target.value)}
              placeholder="Enter loan ID..."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleFilter}>
                Filter
              </Button>
              <Button variant="outlined" onClick={handleClearFilter}>
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>NIC</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {filterName || filterLoanId ? 'No customers found matching the filter criteria.' : 'No customers found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>{customer.id}</TableCell>
                  <TableCell>{customer.fullName || customer.name}</TableCell>
                  <TableCell>{customer.nic}</TableCell>
                  <TableCell>{customer.phoneNumber || customer.phone}</TableCell>
                  <TableCell>
                    {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/officer/customers/${customer.id}`)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerManagement;
